/**
 * 표준 브라우저 `WebSocket` 한 개의 수명주기 + 재연결 백오프.
 *
 * ✅ **서버에 WS 라우트가 실재한다**(2026-09-18 실측 — 이전 기록 「0건」은 낡았다).
 *   `ctcells-core` `app/views/events_stream.py:723` · `app/views/safety_stream.py:191` 에
 *   `@router.websocket` 이 있고 `API_PREFIX` 로 마운트된다. 주소는 이 리포의
 *   `STREAM_PATHS`(`packages/core/src/config.ts`)와 **정확히 일치**한다.
 *
 * 🔴 **핸드셰이크에 자격이 필요하다.** 서버는 `Sec-WebSocket-Protocol: bearer, <토큰>` 을
 *    요구하고, 없으면 **핸드셰이크 자체를 거절**한다(「붙은 뒤 끊는다」가 아니다 —
 *    2026-09-18 실측: 서브프로토콜 없이 열면 `close 1006`). 자격 해석은 `streamAuth.ts` 가 쥔다.
 */
import { realtimeConfig, type ReconnectPolicy } from '@ctcells/core'

import { streamProtocols } from './streamAuth.ts'
import type { StreamConnection, StreamConnectionOptions, StreamStatus } from './types.ts'

/** 테스트가 가짜 소켓을 주입할 수 있게 최소 모양만 요구한다 */
export interface SocketLike {
  close(code?: number, reason?: string): void
  onopen: ((event: unknown) => void) | null
  onclose: ((event: unknown) => void) | null
  onerror: ((event: unknown) => void) | null
  onmessage: ((event: { data: unknown }) => void) | null
}

export type SocketFactory = (url: string, protocols: readonly string[]) => SocketLike

export interface WebSocketConnectionOptions extends StreamConnectionOptions {
  /** 기본은 전역 `WebSocket`. 테스트에서만 바꾼다 */
  socketFactory?: SocketFactory
  reconnect?: ReconnectPolicy
  /**
   * 이번 연결에 쓸 서브프로토콜. 기본은 `streamAuth.ts` 의 등록된 해석기다.
   *
   * 🔴 **값이 아니라 함수다.** 매 연결 시도마다 다시 부른다 — 값으로 굳히면 재연결할 때
   *    **만료된 토큰**을 그대로 다시 쓴다. `null` 을 내면 그 회차는 **소켓을 만들지 않는다.**
   */
  protocols?: () => readonly string[] | null
}

function defaultSocketFactory(url: string, protocols: readonly string[]): SocketLike {
  // ⚠ 토큰은 `protocols` 안에 있다. **URL 에 싣지 않는다** — 쿼리로 넣으면 프록시 로그로 샌다
  return new WebSocket(url, protocols as string[]) as unknown as SocketLike
}

/** JSON 이면 파싱, 아니면 원문 그대로. **파싱 실패로 연결을 끊지 않는다** */
export function parseFrame(data: unknown): unknown {
  if (typeof data !== 'string') return data
  try {
    return JSON.parse(data) as unknown
  } catch {
    return data
  }
}

export function createWebSocketConnection(options: WebSocketConnectionOptions): StreamConnection {
  const { name, url, onStatus, onMessage } = options
  const socketFactory = options.socketFactory ?? defaultSocketFactory
  const policy = options.reconnect ?? realtimeConfig().reconnect
  const resolveProtocols = options.protocols ?? streamProtocols

  let socket: SocketLike | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let attempt = 0
  let disposed = false
  let lastStatus: StreamStatus | null = null

  /** 같은 상태를 반복해서 올리지 않는다 — 자격 대기 중에는 매 시도가 `closed` 라 그대로 두면 재렌더가 샌다 */
  function emit(status: StreamStatus): void {
    if (status === lastStatus) return
    lastStatus = status
    onStatus(status)
  }

  function open(): void {
    if (disposed) return

    // 🔴 **자격을 매 시도마다 다시 읽는다.** 로그인 전이거나 갱신 중이면 `null` 이고,
    //    그때는 **소켓을 아예 만들지 않는다** — 서버가 핸드셰이크를 거절할 것이 뻔한데
    //    때려 봐야 1006 만 쌓인다. 상태는 stale 로 남아 제어는 계속 막힌다(§6).
    const protocols = resolveProtocols()
    if (protocols === null) {
      emit('closed')
      // ⚠ **자격 대기는 백오프를 키우지 않는다.** 소켓도 안 열었으니 서버에 부담이 0 이고,
      //    키워 두면 로그인한 뒤 최대 30초를 기다리게 된다. 실패 백오프(`attempt`)와 구분한다.
      scheduleReconnect(policy.initialDelayMs)
      return
    }

    emit('connecting')

    let created: SocketLike
    try {
      created = socketFactory(url, protocols)
    } catch {
      // 주소가 잘못돼도 앱을 죽이지 않는다. 끊김으로 취급하고 백오프한다
      emit('closed')
      scheduleReconnect()
      return
    }
    socket = created

    created.onopen = () => {
      if (disposed) return
      attempt = 0
      emit('open')
    }
    created.onmessage = (event) => {
      if (disposed) return
      onMessage(parseFrame(event.data))
    }
    created.onerror = () => {
      // close 가 이어서 온다. 여기서 재연결을 걸면 두 번 걸린다
    }
    created.onclose = () => {
      if (disposed) return
      socket = null
      emit('closed')
      scheduleReconnect()
    }
  }

  /**
   * `waitMs` 를 주면 **그 값 그대로** 기다리고 실패 횟수를 세지 않는다(자격 대기 전용).
   * 안 주면 지수 백오프다 — 토큰 만료로 계속 1006 이 나도 `maxDelayMs` 에서 멈춰 폭주하지 않는다.
   */
  function scheduleReconnect(waitMs?: number): void {
    if (disposed || timer !== null) return
    const delay =
      waitMs ??
      Math.min(policy.maxDelayMs, policy.initialDelayMs * Math.pow(policy.factor, attempt))
    if (waitMs === undefined) attempt += 1
    timer = setTimeout(() => {
      timer = null
      open()
    }, delay)
  }

  open()

  return {
    name,
    close() {
      disposed = true
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
      const current = socket
      socket = null
      if (current) {
        current.onopen = null
        current.onmessage = null
        current.onerror = null
        current.onclose = null
        current.close()
      }
      // 종료 통지는 중복이라도 반드시 나간다 — 셸이 이 신호로 정리한다
      lastStatus = null
      emit('closed')
    },
  }
}
